import _ from "lodash";
import React, { Component, Fragment, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";
import { connect } from "react-redux";

import { ModalService } from "@sportal/cdk/modal";
import {
  CollapsibleSection,
  useCollapseContext,
} from "@sportal/cdk/collapsibleSection";
import {
  addSchedule,
  changeSchedule,
  removeSchedule,
  changeEnabled,
  changeCategories,
  changeSchedules,
} from "./schedules.actions";
import { ScheduleRuleList } from "./scheduleRuleList/ScheduleRuleList";
import {
  getAvailableDays,
  getAvailableName,
  getDaysShifted,
  SCHEDULE_LIMIT,
} from "./schedules.selectors";
import { ScheduleCategories } from "./scheduleCategories/ScheduleCategories";
import ScheduleTimeGrid from "./scheduleTimeGrid/ScheduleTimeGrid";
import { Switcher } from "../../../../components/switcher/Switcher";
import { NotMobile, Mobile } from "../../../../hocs/Responsive";
import { SettingsButton } from "../../../../components/settingsButton/SettingsButton";
import { is24HourFormat } from "../../../../store/account";
import { ScheduleCreateDialog } from "./scheduleCreateDialog/scheduleCreateDialog";
import { CreateRuleButton } from "./createRuleButton/CreateRuleButton";
import { ScheduleRulesOverlay } from "./scheduleRulesOverlay/scheduleRulesOverlay";
import { CollapsibleSectionHeader } from "../collapsibleSectionHeader/CollapsibleSectionHeader";
import { CreateSchedulePopover } from "./scheduleCreatePopover/scheduleCreatePopover";

import "./Schedule.scss";

export class ScheduleComponent extends Component {
  state = {
    isOpen: this.props.isActive && !this.props.disabled,
    isPopupOpened: false,
    activeRule: null,
    statusInfo: "",
  };

  componentDidUpdate(prevProps) {
    const { schedules, addSchedule, isActive, disabled } = this.props;

    if (_.isEmpty(schedules)) {
      addSchedule(this.getRuleWithDefaults({ enabled: false }));
    }

    if (prevProps.isActive !== isActive || prevProps.disabled !== disabled) {
      this.setState({ isOpen: isActive && !disabled });
    }
  }

  handleActiveChange = (checked) => {
    const { type, changeEnabled, profile } = this.props;

    this.setState({ isOpen: checked });
    changeEnabled(type, profile, checked);
  };

  getRuleWithDefaults = (rule = {}) => {
    const { type, profile, schedules, firstDayOfWeek } = this.props;

    return {
      type,
      profile,
      enabled: true,
      name: getAvailableName(type, _.map(schedules, "name")),
      day: [getAvailableDays(schedules, firstDayOfWeek)[0]],
      timeStart: "01:00",
      timeEnd: "01:01",
      categories: (schedules[0] && schedules[0].categories) || [],
      ...rule,
    };
  };

  render() {
    const { className, disabled } = this.props;

    const scheduleContainerStyles = classnames("schedule", className);
    const scheduleHeaderStyles = classnames("schedule__header", {
      "schedule__header--disabled": disabled,
    });

    return (
      <div className={scheduleContainerStyles}>
        <NotMobile>{this.renderDesktop()}</NotMobile>
        <Mobile>{this.renderMobile(scheduleHeaderStyles)}</Mobile>
      </div>
    );
  }

  renderDesktop() {
    const {
      description,
      name,
      isActive,
      disabled,
      type,
      changeEnabled,
      profile,
    } = this.props;

    return (
      <Fragment>
        <CollapsibleSection
          defaultOpened={!disabled && isActive}
          header={
            <CollapsibleSectionHeader
              disabled={disabled}
              clickableHeader={<h3>{name}</h3>}
              staticHeader={
                <ActiveToggle
                  name={name}
                  type={type}
                  isActive={isActive}
                  disabled={disabled}
                  changeEnabled={changeEnabled}
                  profile={profile}
                  onToggle={() => this.setState({ isOpen: !isActive })}
                />
              }
              description={
                <Fragment>
                  <p>{description}</p>
                  {disabled && (
                    <div className="schedule__error">
                      <FormattedMessage
                        id={"homework_time_disabled_explanation"}
                      />
                    </div>
                  )}
                </Fragment>
              }
            />
          }
          content={
            <div>
              {this.renderCreateRule()}
              {this.renderDesktopContent()}
              <ContentCloseOnDisable disabled={disabled} />
            </div>
          }
        />
      </Fragment>
    );
  }

  renderDesktopContent() {
    const { isActive, categories, firstDayOfWeek, disabled } = this.props;
    const isScheduleContentDisabled = !isActive || disabled;
    const scheduleContentStyles = classnames("schedule__content", {
      "schedule__content--disabled": isScheduleContentDisabled,
    });

    const ruleProps = {
      days: getDaysShifted(firstDayOfWeek),
      activeRule: this.state.activeRule,
      setActiveRule: (id) => this.setState({ activeRule: id }),
    };

    return (
      <div>
        <div className={scheduleContentStyles} data-testid={"schedule-content"}>
          <div
            className={"schedule__content__timetable"}
            data-testid={"schedule-content-timetable"}
          >
            <ScheduleTimeGrid {...this.props} {...ruleProps} />
            <ScheduleRuleList
              {...this.props}
              {...ruleProps}
              getRuleWithDefaults={(args) => this.getRuleWithDefaults(args)}
              disabled={isScheduleContentDisabled}
            />
          </div>
          {!_.isEmpty(categories) && (
            <ScheduleCategories
              {...this.props}
              disabled={isScheduleContentDisabled}
            />
          )}
        </div>
      </div>
    );
  }

  renderActiveToggle() {
    const { name, type, isActive, disabled } = this.props;

    return (
      <Switcher
        id={type}
        className="schedule__enable-toggle"
        label={<h3>{name}</h3>}
        checked={isActive}
        onChange={() => this.handleActiveChange(!isActive)}
        disabled={disabled}
      />
    );
  }

  renderCreateRule() {
    const { schedules, isActive, addSchedule, type, profile, disabled } =
      this.props;
    const isScheduleDisabled =
      !isActive || schedules.length >= SCHEDULE_LIMIT || disabled;

    return (
      <Fragment>
        <div className={"sr-only"} role={"status"}>
          {this.state.statusInfo && (
            <FormattedMessage id={this.state.statusInfo} />
          )}
        </div>
        <Mobile>
          <CreateRuleButton
            disabled={isScheduleDisabled}
            onClick={() =>
              ModalService.show((modal) => ({
                dialog: (
                  <ScheduleCreateDialog
                    day={[]}
                    modal={modal}
                    {...this.props}
                  />
                ),
              }))
                .then((schedule) => {
                  addSchedule(schedule);
                  this.setState({
                    statusInfo: "aria_draft_schedule_added",
                  });
                })
                .catch(() => null)
            }
          />
        </Mobile>
        <NotMobile>
          <CreateSchedulePopover
            updateStatusInfo={(statusInfo) => this.setState({ statusInfo })}
            disabled={isScheduleDisabled}
            addSchedule={addSchedule}
            schedules={schedules}
            type={type}
            profile={profile}
          />
        </NotMobile>
      </Fragment>
    );
  }

  renderMobile(scheduleHeaderStyles) {
    const { schedules, categories, description, isActive } = this.props;
    const btnProps = {
      color: "secondary",
      className: "schedule__settings-btn",
      size: "large",
      disabled: !isActive,
    };

    return (
      <Fragment>
        <div className={scheduleHeaderStyles}>{this.renderActiveToggle()}</div>
        <div className="schedule__description">{description}</div>
        {this.renderCreateRule()}
        <SettingsButton
          {...btnProps}
          label={<FormattedMessage id={"schedules"} />}
          status={
            <Fragment>
              <FormattedMessage id={"rules"} />
              <span className={"schedule__settings-btn__counter"}>
                {" "}
                {schedules.length}/{SCHEDULE_LIMIT}
              </span>
            </Fragment>
          }
          onClick={() =>
            ModalService.show((modal) => ({
              dialog: <ScheduleRulesOverlay modal={modal} {...this.props} />,
            })).catch(() => null)
          }
        />
        {!_.isEmpty(categories) && <ScheduleCategories {...this.props} />}
      </Fragment>
    );
  }
}

const ContentCloseOnDisable = ({ disabled }) => {
  const { toggleOpened, opened } = useCollapseContext();

  useEffect(() => {
    if (!opened || !disabled) return;

    toggleOpened();
  }, [disabled, opened, toggleOpened]);

  return null;
};

const ActiveToggle = ({
  name,
  type,
  isActive,
  disabled,
  changeEnabled,
  profile,
  onToggle,
}) => {
  const { toggleOpened, opened } = useCollapseContext();
  const handleChange = () => {
    if (opened === isActive) {
      toggleOpened();
      onToggle();
    }
    changeEnabled(type, profile, !isActive);
  };

  return (
    <Switcher
      id={type}
      className="schedule__enable-toggle"
      label={<span className="sr-only">{name}</span>}
      checked={isActive}
      onChange={handleChange}
      disabled={disabled}
    />
  );
};

export const Schedule = connect(
  (state) => {
    const {
      config: { firstDayOfWeek },
    } = state;

    return {
      timeFormat24h: is24HourFormat(state),
      firstDayOfWeek,
      days: getDaysShifted(firstDayOfWeek),
    };
  },
  {
    addSchedule,
    changeSchedule,
    changeSchedules,
    removeSchedule,
    changeEnabled,
    changeCategories,
  }
)(ScheduleComponent);
